import jsPDF from "jspdf";
import "jspdf-autotable";

// Récupération des variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME;
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME;
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1;
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2;
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO;

// Fonction pour formater les prix
const formatPrice = (price) => {
  if (!price && price !== 0) return '0 Fcfa';
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const formattedPrice = numericPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedPrice} Fcfa`;
};

// Fonction pour convertir le montant en lettres
const amountToWords = (amount) => {
  const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
  const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
  const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
  
  if (amount === 0) return 'zéro';
  if (amount > 999999) return 'Montant trop élevé';

  let words = '';

  // Milliers
  const thousands = Math.floor(amount / 1000);
  if (thousands > 0) {
    if (thousands === 1) {
      words += 'mille ';
    } else {
      words += convertHundreds(thousands) + 'mille ';
    }
    amount %= 1000;
  }

  // Centaines
  if (amount > 0) {
    words += convertHundreds(amount);
  }

  return words.trim() + ' Francs CFA';

  function convertHundreds(num) {
    let result = '';
    
    // Centaines
    const hundreds = Math.floor(num / 100);
    if (hundreds > 0) {
      if (hundreds === 1) {
        result += 'cent ';
      } else {
        result += units[hundreds] + ' cent ';
      }
      num %= 100;
    }

    // Décimales
    if (num > 0) {
      if (result !== '') result += '';
      
      if (num < 10) {
        result += units[num];
      } else if (num < 20) {
        result += teens[num - 10];
      } else {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        
        if (ten === 7 || ten === 9) {
          result += tens[ten] + '-' + teens[unit];
        } else {
          result += tens[ten];
          if (unit > 0) {
            if (ten === 8) {
              result += '-' + units[unit];
            } else {
              result += (ten === 1 ? ' et ' : '-') + units[unit];
            }
          }
        }
      }
    }

    return result;
  }
};

// Fonction pour grouper les produits par référence et créateur
const groupProductsByReference = (reportProd) => {
  const groups = {};
  
  reportProd.forEach(prod => {
    const key = `${prod.reference || 'N/A'}_${prod.createur || 'N/A'}`;
    
    if (!groups[key]) {
      groups[key] = {
        reference: prod.reference,
        createur: prod.createur,
        produits: []
      };
    }
    
    groups[key].produits.push(prod);
  });
  
  return Object.values(groups);
};

// Fonction pour créer l'en-tête sobre en noir et blanc
const createModernHeader = (doc, reportProd, clientInfo) => {
  const blackColor = [0, 0, 0];
  const darkGray = [80, 80, 80];
  const lightGray = [240, 240, 240];

  // Calcul des variables
  let total = 0;
  let total_client = 0;
  let total_ipm = 0;
  let ipm = 0;
  let client = 0;
  let remise = 0;
  let numero = '';
  let nom = '';
  let telephone = '';
  let adresse = '';
  let beneficiaire = '';
  let vendeur = '';

  // Déterminer si c'est un client IPM RIDWAN
  const isIPMType = clientInfo?.designation?.toUpperCase() === "IPM RIDWAN";

  if (!isIPMType) {
    reportProd.forEach(prod => {
      total = total + prod.total;
      remise = prod.remise;
      numero = prod.numFactSrt;
      nom = prod.nom;
      telephone = prod.telephone;
      adresse = prod.adresse;
      vendeur = prod.vendeur;
      beneficiaire = prod.beneficiaire;
    });
  } else {
    reportProd.forEach(prod => {
      client = client + prod.total_client;
      ipm = ipm + prod.total_ipm;
      total = total + prod.total;
      numero = prod.numFactSrt;
      nom = prod.nom;
      telephone = prod.telephone;
      adresse = prod.adresse;
      vendeur = prod.vendeur;
    });
  }

  // En-tête sobre - fond blanc
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 80, 'F');

  // Titre principal en noir
  doc.setFontSize(20);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', 105, 25, { align: 'center' });

  // Informations pharmacie en noir
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(PHARMACY_NAME, 105, 35, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(OWNER_NAME, 105, 40, { align: 'center' });
  doc.text(`${ADDRESS_LINE1} - ${ADDRESS_LINE2}`, 105, 45, { align: 'center' });
  doc.text(BUSINESS_INFO, 105, 50, { align: 'center' });

  // Numéro de facture et date
  const d = new Date();
  const datestring = ("0" + d.getDate()).slice(-2) + "/" + ("0" + (d.getMonth() + 1)).slice(-2) + "/" + 
                    d.getFullYear() + " " + ("0" + d.getHours()).slice(-2) + ":" + ("0" + d.getMinutes()).slice(-2);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`FACTURE N°: ${numero}`, 15, 65);
  doc.text(`DATE: ${datestring}`, 150, 65);

  // Carte d'informations client sobre
  doc.setFillColor(255, 255, 255); // Fond blanc
  doc.roundedRect(15, 85, 180, 45, 3, 3, 'F');
  
  doc.setDrawColor(...blackColor); // Bordure noire
  doc.setLineWidth(0.5);
  doc.roundedRect(15, 85, 180, 45, 3, 3, 'D');

  // Titre section client
  doc.setFontSize(11);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('INFORMATIONS CLIENT', 25, 95);

  // Informations client
  doc.setFontSize(9);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'normal');
  
  // Colonne gauche
  doc.text(`Nom: ${nom || 'Non spécifié'}`, 25, 105);
  doc.text(`Téléphone: ${telephone || 'Non spécifié'}`, 25, 113);
  
  // Colonne droite
  doc.text(`Adresse: ${adresse || 'Non spécifiée'}`, 110, 105);
  if (vendeur) {
    doc.text(`Vendeur: ${vendeur}`, 110, 113);
  }

  // Informations spécifiques au type
  if (isIPMType) {
    doc.text(`Part Client: ${formatPrice(client)}`, 25, 121);
    doc.text(`Part IPM: ${formatPrice(ipm)}`, 110, 121);
  } else {
    if (remise > 0) {
      doc.text(`Remise appliquée: ${remise}%`, 25, 121);
    }
    if (beneficiaire) {
      doc.text(`Bénéficiaire: ${beneficiaire}`, 110, 121);
    }
  }

  return {
    doc,
    total,
    remise,
    client,
    ipm,
    nom,
    datestring,
    blackColor,
    darkGray,
    lightGray,
    isIPMType
  };
};

// Fonction pour créer la section récapitulative (DYNAMIQUE)
const createSummarySection = (doc, reportProd, tableBottom, blackColor, darkGray, lightGray, isIPMType) => {
  let total = 0;
  let client = 0;
  let ipm = 0;
  let remise = 0;

  // Recalculer les totaux
  if (!isIPMType) {
    reportProd.forEach(prod => {
      total += prod.total;
      remise = prod.remise;
    });
  } else {
    reportProd.forEach(prod => {
      client += prod.total_client;
      ipm += prod.total_ipm;
      total += prod.total;
    });
  }

  const netAPayer = Math.round(total - (remise * total) / 100);
  
  // Calculer la hauteur nécessaire pour la section récapitulative
  const summaryHeight = !isIPMType ? 
    (remise > 0 ? 50 : 40) : // Avec ou sans remise
    50; // Pour IPM RIDWAN (client + IPM)

  // Vérifier s'il y a assez d'espace sur la page actuelle
  const pageHeight = doc.internal.pageSize.height;
  const currentY = tableBottom + 15;
  
  // Si pas assez d'espace pour le récapitulatif + signature (90px), ajouter une page
  if (currentY + summaryHeight + 60 > pageHeight) {
    doc.addPage();
    return createSummarySection(doc, reportProd, 20, blackColor, darkGray, lightGray, isIPMType);
  }

  // Position Y pour la section récapitulative
  const summaryY = currentY;

  // Cadre des totaux design sobre
  doc.setFillColor(255, 255, 255); // Fond blanc
  doc.roundedRect(120, summaryY, 75, summaryHeight, 3, 3, 'F');
  doc.setDrawColor(...blackColor); // Bordure noire
  doc.setLineWidth(0.5);
  doc.roundedRect(120, summaryY, 75, summaryHeight, 3, 3, 'D');

  // Titre section totaux
  doc.setFontSize(10);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('RÉCAPITULATIF', 125, summaryY + 8);

  // Ligne séparatrice
  doc.setDrawColor(...blackColor);
  doc.setLineWidth(0.3);
  doc.line(125, summaryY + 10, 190, summaryY + 10);

  // Détails des totaux
  doc.setFontSize(9);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'normal');
  
  let yOffset = summaryY + 18;
  
  if (!isIPMType) {
    doc.text(`Sous-total:`, 125, yOffset);
    doc.text(formatPrice(total), 170, yOffset, { align: 'right' });
    
    if (remise > 0) {
      doc.text(`Remise (${remise}%):`, 125, yOffset + 8);
      doc.text(`- ${formatPrice((remise * total) / 100)}`, 170, yOffset + 8, { align: 'right' });
      yOffset += 8;
    }
  } else {
    doc.text(`Part Client:`, 125, yOffset);
    doc.text(formatPrice(client), 170, yOffset, { align: 'right' });
    
    doc.text(`Part IPM:`, 125, yOffset + 8);
    doc.text(formatPrice(ipm), 170, yOffset + 8, { align: 'right' });
    yOffset += 8;
  }

  // Total net en évidence
  doc.setFontSize(10);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('NET À PAYER:', 125, yOffset + 10);
  doc.text(formatPrice(netAPayer), 170, yOffset + 10, { align: 'right' });

  return {
    netAPayer,
    summaryBottom: summaryY + summaryHeight
  };
};

// Fonction pour créer le pied de page sobre (DYNAMIQUE) - CORRIGÉE
const createModernFooter = (doc, total, remise, netAPayer, pageCount, pharmacyName, summaryBottom) => {
  const blackColor = [0, 0, 0];
  const lightGray = [240, 240, 240];
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    const pageHeight = doc.internal.pageSize.height;
    let signatureY;

    // LA SIGNATURE UNIQUEMENT SUR LA DERNIÈRE PAGE
    if (i === pageCount) {
      // Sur la dernière page, placer la signature après le récapitulatif avec un bon espace
      signatureY = summaryBottom + 25;
      
      // Vérifier si on dépasse la page
      if (signatureY + 40 > pageHeight) {
        // Pas assez d'espace, ajouter une nouvelle page pour la signature
        doc.addPage();
        signatureY = 40;
        pageCount = doc.internal.getNumberOfPages();
      }

      // Section de signature sobre UNIQUEMENT SUR LA DERNIÈRE PAGE
      doc.setFillColor(255, 255, 255); // Fond blanc
      doc.rect(15, signatureY, 180, 40, 'F');
      
      doc.setDrawColor(...blackColor); // Bordure noire
      doc.setLineWidth(0.2);
      doc.rect(15, signatureY, 180, 40, 'D');

      // Montant en lettres
      doc.setFontSize(8);
      doc.setTextColor(...blackColor);
      doc.setFont('helvetica', 'italic');
      doc.text(`Arrêtée à la somme de: ${amountToWords(netAPayer).toUpperCase()}`, 20, signatureY + 15);

      // Signature
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Signature et cachet', 160, signatureY + 25, { align: 'center' });
      doc.line(145, signatureY + 28, 175, signatureY + 28);
    }

    // Information de page (sur toutes les pages)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...blackColor);
    doc.text(
      `Page ${i} sur ${pageCount} - ${pharmacyName} - Généré le ${new Date().toLocaleDateString('fr-FR')}`,
      105,
      pageHeight - 10,
      { align: 'center' }
    );
  }
};

// Fonction pour créer le tableau avec regroupement par référence
const createGroupedTable = (doc, reportProd, startY, blackColor, darkGray, lightGray, isIPMType) => {
  // Grouper les produits par référence et créateur
  const groupedProducts = groupProductsByReference(reportProd);
  
  let rowIndex = 1;

  // Définir les colonnes selon le type
  const tableColumns = !isIPMType 
    ? ["N°", "Produit", "Quantité", "Prix Unitaire", "Total", "Statut"]
    : ["N°", "Produit", "Quantité", "Prix Unitaire", "Statut", "Part Client", "Part IPM"];

  // Préparer toutes les lignes du tableau
  const allTableRows = [];

  groupedProducts.forEach((group, groupIndex) => {
    // Ajouter une ligne d'en-tête pour le groupe (référence et créateur)
    allTableRows.push([
      {
        content: `Réf: ${group.reference || 'N/A'} | Créateur: ${group.createur || 'N/A'}`,
        colSpan: tableColumns.length,
        styles: {
          fillColor: [255, 255, 255], // Fond blanc
          textColor: blackColor, // Texte noir
          fontStyle: 'bold',
          halign: 'left',
          cellPadding: 3
        }
      }
    ]);

    // Ajouter les produits de ce groupe
    group.produits.forEach((prod, prodIndex) => {
      const baseRow = [
        rowIndex++,
        prod.nomProd,
        prod.qtVdu,
        formatPrice(prod.prixProdVendu)
      ];

      if (!isIPMType) {
        baseRow.push(
          formatPrice(prod.prixProdVendu * prod.qtVdu),
          prod.statut
        );
      } else {
        baseRow.push(
          prod.statut,
          formatPrice(prod.total_client),
          formatPrice(prod.total_ipm)
        );
      }

      allTableRows.push(baseRow);
    });

    // Ajouter une ligne vide entre les groupes (sauf pour le dernier groupe)
    if (groupIndex < groupedProducts.length - 1) {
      allTableRows.push(Array(tableColumns.length).fill(''));
    }
  });

  // Générer le tableau avec autoTable
  doc.autoTable({
    startY: startY,
    head: [tableColumns],
    body: allTableRows,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: blackColor, // Lignes noires
      lineWidth: 0.1,
      font: 'helvetica',
      minCellHeight: 6,
      textColor: blackColor // Texte noir
    },
    headStyles: {
      fillColor: [255, 255, 255], // Fond blanc
      textColor: blackColor, // Texte noir
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
      cellPadding: 3,
      lineColor: blackColor // Bordures noires
    },
    bodyStyles: {
      textColor: blackColor, // Texte noir
      cellPadding: 3
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255], // Fond blanc pour toutes les lignes
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 'auto', halign: 'left' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: 15, right: 15 },
    tableLineColor: blackColor, // Bordures de tableau noires
    tableLineWidth: 0.5,
    didParseCell: function(data) {
      if (data.row.raw[0] && typeof data.row.raw[0] === 'object' && data.row.raw[0].content) {
        data.cell.styles = data.row.raw[0].styles;
        data.cell.text = data.row.raw[0].content;
      }
    }
  });

  return doc.lastAutoTable.finalY;
};

// Fonction principale sobre en noir et blanc
const generatePDFBon = (reportProd, clientInfo = null) => {
  // Initialiser jsPDF
  const doc = new jsPDF();

  // Variables pour les totaux
  let total = 0;
  let remise = 0;
  let numero = '';
  let nom = '';

  // Déterminer si c'est un client IPM RIDWAN
  const isIPMType = clientInfo?.designation?.toUpperCase() === "IPM RIDWAN";

  // Calculer les totaux initiaux
  if (!isIPMType) {
    reportProd.forEach(prod => {
      total += prod.total;
      remise = prod.remise;
      numero = prod.numFactSrt;
      nom = prod.nom;
    });
  } else {
    reportProd.forEach(prod => {
      total += prod.total;
      numero = prod.numFactSrt;
      nom = prod.nom;
    });
  }

  // Créer l'en-tête sobre
  const { blackColor, darkGray, lightGray, isIPMType: headerIsIPMType } = createModernHeader(doc, reportProd, clientInfo);

  // Créer le tableau avec regroupement
  const startY = 140;
  const tableBottom = createGroupedTable(doc, reportProd, startY, blackColor, darkGray, lightGray, headerIsIPMType);

  // Créer la section récapitulative (DYNAMIQUE)
  const { netAPayer, summaryBottom } = createSummarySection(
    doc, 
    reportProd, 
    tableBottom, 
    blackColor, 
    darkGray, 
    lightGray, 
    headerIsIPMType
  );

  // Pied de page sobre avec la position finale
  createModernFooter(
    doc, 
    total, 
    remise, 
    netAPayer, 
    doc.internal.getNumberOfPages(), 
    PHARMACY_NAME, 
    summaryBottom
  );

  // Sauvegarde avec nom de fichier amélioré
  const d = new Date();
  const datestring = d.getFullYear() + "-" + 
                    ("0" + (d.getMonth() + 1)).slice(-2) + "-" + 
                    ("0" + d.getDate()).slice(-2) + "_" + 
                    ("0" + d.getHours()).slice(-2) + 
                    ("0" + d.getMinutes()).slice(-2);

  const clientName = nom ? nom.replace(/[^a-zA-Z0-9]/g, '_') : 'client';
  doc.save(`Facture_${numero}_${clientName}_${datestring}.pdf`);
};

export default generatePDFBon;

// Export des fonctions auxiliaires
export { formatPrice, amountToWords, groupProductsByReference, createModernHeader, createSummarySection, createModernFooter, createGroupedTable };